import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchSalesByOrganizationAndBranch,
  fetchSalesSummaryByOrganizationAndBranch,
} from "../../api/sales";
import { storage } from "../../helpers/storage";

const initialState = {
  value: {
    sales: [],
    offlineSales: [],
    lastPage: 1,
    currentPage: 1,
    limit: process.env.REACT_APP_PAGE_LIMIT || 30,
    loading: false,
    loadingNextPage: false,
    loadingBackground: false,
    loadBackgroundFailed: false,
    salesSummary: {
      total_sales: 0,
      total_revenue: 0.0,
    },
    lastParams: {
      organization_id: null,
      branch_id: null,
      filter_type: "",
      payment_methods: "",
      start_date: "",
      end_date: "",
      keyword: "",
      type: "",
    },
    error: null,
  },
};

export const loadSales = createAsyncThunk(
  "sales/loadSales",
  async (
    {
      organization_id,
      branch_id,
      filter_type,
      payment_methods,
      start_date,
      end_date,
      keyword,
      type,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState().saleReducer;

      const limit = state.value.limit;

      const response = await fetchSalesByOrganizationAndBranch(
        organization_id,
        branch_id,
        filter_type,
        payment_methods,
        start_date,
        end_date,
        keyword,
        1,
        limit,
        type
      );

      return {
        sales: response.data?.sales?.data || [],
        lastPage: response.data?.lastPage || 1,
        newParams: {
          organization_id: organization_id,
          branch_id,
          filter_type,
          payment_methods,
          start_date,
          end_date,
          keyword: keyword,
          type: type,
        },
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch sales.");
    }
  }
);

export const reloadSalesInBackground = createAsyncThunk(
  "sales/reloadSalesInBackground",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().saleReducer;

      const {
        lastParams: {
          organization_id,
          branch_id,
          filter_type,
          payment_methods,
          start_date,
          end_date,
          keyword,
          type,
        },
        currentPage,
        limit,
        lastPage,
      } = state.value;

      const totalLimit = currentPage > 3 ? limit * 3 : currentPage * limit;
      const finalCurrentPage = currentPage > 3 ? 3 : currentPage;

      const response = await fetchSalesByOrganizationAndBranch(
        organization_id,
        branch_id,
        filter_type,
        payment_methods,
        start_date,
        end_date,
        keyword,
        1,
        totalLimit,
        type
      );

      return {
        sales: response.data?.sales?.data || [],
        lastPage: lastPage,
        currentPage: finalCurrentPage,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch background sales."
      );
    }
  }
);

export const reloadSalesSummaryInBackground = createAsyncThunk(
  "sales/reloadSalesSummaryInBackground",
  async (
    {
      organization_id,
      branch_id,
      filter_type,
      payment_methods,
      start_date,
      end_date,
      type,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetchSalesSummaryByOrganizationAndBranch(
        organization_id,
        branch_id,
        filter_type || "last_30_days",
        payment_methods,
        start_date,
        end_date,
        "",
        "",
        type
      );

      return {
        total_sales: response.data?.total_sales || 0,
        total_revenue: response.data?.total_revenue || 0.0,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch background sales summary."
      );
    }
  }
);

export const loadSalesIfNeeded = createAsyncThunk(
  "sales/loadSalesIfNeeded",
  async (
    {
      organization_id,
      branch_id,
      filter_type,
      payment_methods,
      start_date,
      end_date,
      keyword,
      type,
    },
    { getState, dispatch }
  ) => {
    const state = getState().saleReducer;

    const currentParams = {
      organization_id,
      branch_id,
      filter_type,
      payment_methods,
      start_date,
      end_date,
      keyword,
      type,
    };
    const previousParams = {
      organization_id: state.value.lastParams?.organization_id,
      branch_id: state.value.lastParams?.branch_id,
      filter_type: state.value.lastParams?.filter_type,
      payment_methods: state.value.lastParams?.payment_methods,
      start_date: state.value.lastParams?.start_date,
      end_date: state.value.lastParams?.end_date,
      keyword: state.value.lastParams?.keyword,
      type: state.value.lastParams?.type,
    };

    let storageSalesSummary = await storage.get("salesSummary");
    storageSalesSummary = storageSalesSummary
      ? JSON.parse(storageSalesSummary)
      : null;

    if (storageSalesSummary) {
      dispatch(
        updateSalesSummary({
          total_sales: storageSalesSummary?.total_sales || 0,
          total_revenue: storageSalesSummary?.total_revenue || 0.0,
        })
      );
    }

    let storageSales = await storage.get("sales");
    storageSales = storageSales ? JSON.parse(storageSales) : null;

    let storageOfflineSales = await storage.get("offlineSales");
    storageOfflineSales = storageOfflineSales
      ? JSON.parse(storageOfflineSales)
      : null;

    if (storageSales && storageSales?.length) {
      if (storageOfflineSales && storageOfflineSales?.length) {
        dispatch(updateOfflineSales({ sales: storageOfflineSales }));
      }
      dispatch(updateSales({ sales: storageSales }));
      dispatch(updateSaleLoading(false));
    } else if (
      JSON.stringify(currentParams) !== JSON.stringify(previousParams)
    ) {
      dispatch(updateSaleLoading(true));
    }

    if (
      previousParams.branch_id &&
      previousParams.branch_id !== currentParams.branch_id
    ) {
      dispatch(updateSaleLoading(true));
    }

    dispatch(
      reloadSalesSummaryInBackground({
        organization_id,
        branch_id,
        filter_type,
        payment_methods,
        start_date,
        end_date,
        type,
      })
    );
    if (JSON.stringify(currentParams) === JSON.stringify(previousParams)) {
      return dispatch(reloadSalesInBackground());
    }

    return dispatch(
      loadSales({
        organization_id,
        branch_id,
        filter_type,
        payment_methods,
        start_date,
        end_date,
        keyword,
        type,
      })
    );
  }
);

export const loadNextSales = createAsyncThunk(
  "sales/loadNextSales",
  async ({ page }, { getState, rejectWithValue }) => {
    try {
      const state = getState().saleReducer;

      const limit = state.value.limit;
      const keyword = state.value.lastParams?.keyword;
      const organization_id = state.value.lastParams?.organization_id;
      const branch_id = state.value.lastParams?.branch_id;
      const filter_type = state.value.lastParams?.filter_type;
      const payment_methods = state.value.lastParams?.payment_methods;
      const start_date = state.value.lastParams?.start_date;
      const end_date = state.value.lastParams?.end_date;
      const type = state.value.lastParams?.type;

      if (state.value.currentPage === page) {
        return {
          sales: [],
          lastPage: state.value.lastPage,
          currentPage: page,
        };
      } else {
        const response = await fetchSalesByOrganizationAndBranch(
          organization_id,
          branch_id,
          filter_type,
          payment_methods,
          start_date,
          end_date,
          keyword,
          page,
          limit,
          type
        );

        return {
          sales: response.data?.sales?.data || [],
          lastPage: response.data?.lastPage,
          currentPage: page,
        };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch sales.");
    }
  }
);

const sale = createSlice({
  name: "sales",
  initialState,
  reducers: {
    updateSales: (state, data) => {
      state.value.sales = data.payload.sales;
    },
    updateOfflineSales: (state, data) => {
      state.value.offlineSales = data.payload.sales;
    },
    updateSalesSummary: (state, data) => {
      state.value.salesSummary = {
        total_sales: data.payload.total_sales,
        total_revenue: data.payload.total_revenue,
      };
    },
    addNewSale: (state, data) => {
      const { sale } = data.payload;

      state.value.sales?.unshift(sale);
      storage.set("sales", JSON.stringify(state.value.sales));
    },
    addNewOfflineSale: (state, data) => {
      const { sale } = data.payload;
      state.value.sales?.unshift(sale);
      storage.set("sales", JSON.stringify(state.value.sales));

      state.value.offlineSales?.unshift(sale);
      storage.set("offlineSales", JSON.stringify(state.value.offlineSales));
    },
    removeOfflineSale: (state, data) => {
      const { sale } = data.payload;

      const saleIndex = state.value.sales?.findIndex(
        (el) => JSON.stringify(el) === JSON.stringify(sale)
      );
      const offlineSaleIndex = state.value.offlineSales?.findIndex(
        (el) => JSON.stringify(el) === JSON.stringify(sale)
      );

      if (saleIndex !== -1) {
        state.value.sales?.splice(saleIndex, 1);
      }

      if (offlineSaleIndex !== -1) {
        state.value.offlineSales?.splice(offlineSaleIndex, 1);
      }

      storage.set("sales", JSON.stringify(state.value.sales));
      storage.set("offlineSales", JSON.stringify(state.value.offlineSales));
    },
    updateExistingSale: (state, data) => {
      const { sale } = data.payload;
      const exists = state.value.sales?.findIndex((el) => el?.id === sale?.id);

      if (exists !== -1) {
        state.value.sales[exists] = sale;
      }
      storage.set("sales", JSON.stringify(state.value.sales));
    },
    updateSaleLoading: (state, data) => {
      state.value.loading = data.payload;
      state.value.error = null;
    },
    deleteExistingSale: (state, data) => {
      const { sale } = data.payload;
      const exists = state.value.sales?.findIndex((el) => el?.id === sale?.id);

      if (exists !== -1) {
        state.value.sales?.splice(exists, 1);
      }

      storage.set("sales", JSON.stringify(state.value.sales));
    },
    clearSales: () => {
      return {
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(reloadSalesSummaryInBackground.pending, (state) => {})
      .addCase(reloadSalesSummaryInBackground.fulfilled, (state, action) => {
        state.value.salesSummary = {
          total_sales: action.payload.total_sales,
          total_revenue: action.payload.total_revenue,
        };

        storage.set("salesSummary", JSON.stringify(state.value.salesSummary));
      })
      .addCase(reloadSalesSummaryInBackground.rejected, (state, action) => {});

    builder
      .addCase(loadSales.pending, (state) => {})
      .addCase(loadSales.fulfilled, (state, action) => {
        const previousOfflineSales =
          state.value.offlineSales?.filter(
            (sale) => sale?.branch_id === action.payload.newParams?.branch_id
          ) || [];
        const newSales = [...previousOfflineSales, ...action.payload.sales];

        state.value.loading = false;
        state.value.sales = newSales;
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = 1;
        state.value.lastParams = action.payload.newParams;

        storage.set("sales", JSON.stringify(newSales));
      })
      .addCase(loadSales.rejected, (state, action) => {
        state.value.loading = false;
        state.value.error = action.payload;
        state.value.lastPage = state.value.currentPage;
      });

    builder
      .addCase(loadNextSales.pending, (state) => {
        state.value.loadingNextPage = true;
        state.value.error = null;
      })
      .addCase(loadNextSales.fulfilled, (state, action) => {
        state.value.loadingNextPage = false;
        state.value.sales = [...state.value.sales, ...action.payload.sales];
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = action.payload.currentPage;

        storage.set(
          "sales",
          JSON.stringify([...state.value.sales, ...action.payload.sales])
        );
      })
      .addCase(loadNextSales.rejected, (state, action) => {
        state.value.loadingNextPage = false;
        state.value.error = action.payload;
        state.value.lastPage = state.value.currentPage;
      });

    builder
      .addCase(reloadSalesInBackground.pending, (state) => {
        state.value.loadingBackground = true;
        state.value.loadBackgroundFailed = false;
      })
      .addCase(reloadSalesInBackground.fulfilled, (state, action) => {
        const previousOfflineSales =
          state.value.offlineSales?.filter(
            (sale) => sale?.branch_id === state.value.lastParams.branch_id
          ) || [];
        const newSales = [...previousOfflineSales, ...action.payload.sales];

        state.value.loadingBackground = false;
        state.value.sales = newSales;
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = action.payload.currentPage;

        storage.set("sales", JSON.stringify(newSales));
      })
      .addCase(reloadSalesInBackground.rejected, (state, action) => {
        state.value.loadingBackground = false;
        state.value.error = action.payload;
        state.value.loadBackgroundFailed = true;
        state.value.lastPage = state.value.currentPage;
      });
  },
});

export const {
  updateSales,
  updateSalesSummary,
  addNewSale,
  addNewOfflineSale,
  updateExistingSale,
  updateSaleLoading,
  deleteExistingSale,
  clearSales,
  removeOfflineSale,
  updateOfflineSales,
} = sale.actions;

export default sale.reducer;
